// Applies a workshop's accent colour to the running app.
//
// The UI is built on Tailwind's teal utilities, which are baked into the
// stylesheet at build time. Rather than rewrite hundreds of class names, we
// emit one <style> block that overrides exactly those utilities with shades
// derived from the chosen colour. Only workshops that pick a colour are
// affected; everyone else keeps the stylesheet as shipped.

const hexToRgb = (hex) => {
  const h = hex.replace('#', '');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
};

// Travel towards white (amount > 0) or black (amount < 0), 0..1.
const shadeRgb = (hex, amount) => {
  const [r, g, b] = hexToRgb(hex);
  const mix = (c) => (amount >= 0
    ? Math.round(c + (255 - c) * amount)
    : Math.round(c * (1 + amount)));
  return [mix(r), mix(g), mix(b)];
};

const rgb = (parts) => `rgb(${parts[0]} ${parts[1]} ${parts[2]})`;
const rgba = (parts, alpha) => `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha})`;
const rgbToHex = (parts) => `#${parts.map((c) => c.toString(16).padStart(2, '0')).join('')}`;

// Tailwind shade -> how far from the base 600 we travel.
const STEPS = {
  50: 0.92, 100: 0.84, 200: 0.68, 300: 0.48, 400: 0.24,
  500: 0.1, 600: 0, 700: -0.16, 800: -0.32, 900: -0.46, 950: -0.62,
};

// Opacity modifiers actually used in the app chrome (`bg-teal-500/20` etc.).
// Tailwind compiles these to their own class names, so each needs its own rule.
const ALPHAS = [10, 15, 20, 30, 40, 50, 60, 70, 80, 90];

// `.admin-scope` and `.staff-scope` keep this off the public marketing pages,
// which carry the product's brand rather than any one workshop's. The
// `.admin-scope.admin-light` variant is needed too: App.css re-asserts several
// teal utilities at that higher specificity for light admin pages, and a
// single-class override would silently lose to it.
const SCOPES = ['.admin-scope', '.admin-scope.admin-light', '.staff-scope',
                // The sidebar keeps a dark palette on light pages via its own
                // four-class overrides in App.css, which outrank everything
                // above — the accent has to be re-stated at that depth.
                '.admin-scope.admin-light .admin-sidebar-dark',
                // Dark mode remaps the teal utilities too (styles/dark.css at
                // (0,2,1)); these score (0,3,1) so a brand colour still wins.
                'html.app-dark .admin-scope', 'html.app-dark .staff-scope'];

const STYLE_ID = 'driveline-accent';

// Perceived lightness, 0..1 — enough to tell a colour that will vanish on a
// dark background from one that will not.
const luminance = (hex) => {
  const [r, g, b] = hexToRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

let currentHex = '';
let darkMode = false;

const render = () => {
  const existing = document.getElementById(STYLE_ID);
  const hex = currentHex;
  if (!hex) {
    if (existing) existing.remove();
    return;
  }
  // A deep brand colour is unreadable as text on a dark surface, so in dark
  // mode the whole ramp is derived from a lifted version of it. The hue the
  // workshop chose survives; only its lightness is re-tuned.
  const base = (darkMode && luminance(hex) < 0.5)
    ? rgbToHex(shadeRgb(hex, 0.34))
    : hex;
  const rules = [`:root { --brand-accent: ${hex}; }`];
  const baseParts = hexToRgb(base);
  // On dark, text has to come off the light end of the ramp and the pale
  // tones stop being surfaces you can read on — they become washes.
  const ink = rgb(shadeRgb(base, 0.45));
  const WASH = { 50: 0.12, 100: 0.2, 200: 0.3 };
  Object.entries(STEPS).forEach(([tone, amount]) => {
    const parts = shadeRgb(base, amount);
    const colour = rgb(parts);
    const pale = darkMode && Number(tone) <= 200;
    const deep = darkMode && Number(tone) >= 600;
    const fill = pale ? rgba(baseParts, WASH[tone]) : colour;
    const line = pale ? rgba(baseParts, 0.32) : colour;
    const text = deep ? ink : colour;
    SCOPES.forEach((scope) => {
      rules.push(
        `${scope} .bg-teal-${tone} { background-color: ${fill} !important; }`,
        `${scope} .text-teal-${tone} { color: ${text} !important; }`,
        `${scope} .border-teal-${tone} { border-color: ${line} !important; }`,
        `${scope} .ring-teal-${tone} { --tw-ring-color: ${colour} !important; }`,
        // Only the `from` stop. Tailwind's own class already declares the
        // transparent `to` stop and the stops list; re-declaring them with
        // !important here beat the real `.to-*` rules (teal ones by source
        // order, non-teal ones outright) and faded every gradient to nothing.
        `${scope} .from-teal-${tone} { --tw-gradient-from: ${colour} !important; }`,
        `${scope} .to-teal-${tone} { --tw-gradient-to: ${colour} !important; }`,
        `${scope} .via-teal-${tone} { --tw-gradient-stops: var(--tw-gradient-from), ${colour}, var(--tw-gradient-to) !important; }`,
        `${scope} .hover\\:bg-teal-${tone}:hover { background-color: ${fill} !important; }`,
        `${scope} .hover\\:text-teal-${tone}:hover { color: ${text} !important; }`,
        `${scope} .hover\\:border-teal-${tone}:hover { border-color: ${line} !important; }`,
      );
      ALPHAS.forEach((a) => {
        const translucent = pale ? fill : rgba(parts, a / 100);
        rules.push(
          `${scope} .bg-teal-${tone}\\/${a} { background-color: ${translucent} !important; }`,
          `${scope} .text-teal-${tone}\\/${a} { color: ${deep ? ink : translucent} !important; }`,
          `${scope} .border-teal-${tone}\\/${a} { border-color: ${pale ? line : translucent} !important; }`,
          `${scope} .hover\\:bg-teal-${tone}\\/${a}:hover { background-color: ${translucent} !important; }`,
        );
      });
    });
  });

  const tag = existing || document.createElement('style');
  tag.id = STYLE_ID;
  tag.textContent = rules.join('\n');
  if (!existing) document.head.appendChild(tag);
};

export const applyAccent = (hex) => {
  currentHex = hex || '';
  render();
};

/** Re-derive the accent for the current theme. Called by ThemeProvider. */
export const setAccentDark = (on) => {
  if (darkMode === Boolean(on)) return;
  darkMode = Boolean(on);
  render();
};
